import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import * as jwt_decode from 'jwt-decode';
import {
    Badge,
    Body1,
    Button,
    Caption1,
    Card,
    Divider,
    Dropdown,
    Field,
    Option,
    OverlayDrawer,
    DrawerHeader,
    DrawerHeaderTitle,
    DrawerBody,
    Spinner,
    Subtitle2,
    Tab,
    TabList,
    Text,
    Title3,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import {
    ArrowClockwiseRegular,
    ArrowResetRegular,
    DataTrendingRegular,
    DismissRegular,
    FilterRegular,
    MoneyRegular,
    ArrowTrendingRegular,
    ArrowDownloadRegular,
    BuildingMultipleRegular,
    DocumentTableRegular,
} from '@fluentui/react-icons';

import * as ComponentFuncitons from './ProfitChar.Functions';
import ProtocolSelectorComponent from '../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent';
import { SponsorSelectorComponent } from '../../../commons/component/Inputs/SponsorSelector/SponsorSelectorComponet';
import DateRangeSelectorComponent from '../../../commons/component/Inputs/DateRangeSelector/DateRangeSelector.Component';
import * as CashFlowStatisticsService from '../../../services/CashFlow-Statistics.Services';
import {
    AgingBucketStatisticViewModel,
    CashflowSeriesPointViewModel,
    MonthlyCashflowStatisticViewModel,
    OverviewStatisticViewModel,
    PaymentDelayStatisticViewModel,
    StatisticsFilter,
    StatisticsTimeSeriesFilter,
    TimeGranularity,
    TopEntityStatisticViewModel,
} from '../../../models/Entities/CashFlow/Statistics/StatisticsModels';

// Backwards-compat: kept so older imports do not break.
export interface ProtocolProfitDatum {
    protocolId: number | string;
    name: string;
    revenue: number;
    cost: number;
}

type OrderMode = 'margin' | 'roi' | 'profit';

interface ProtocolProfit {
    protocolId: number;
    name: string;
    revenue: number;
    cost: number;
    profit: number;
    margin: number;
    roi: number;
}

const CHART_HEIGHT_LARGE = 260;
const CHART_HEIGHT_MEDIUM = 210;
const CHART_HEIGHT_SMALL = 200;

// Unified analyst palette
const CHART_COLORS = {
    income: '#10b981',
    incomeSoft: '#34d399',
    outcome: '#ef4444',
    outcomeSoft: '#f87171',
    net: '#0f6cbd',
    netSoft: '#3b82f6',
    cumulative: '#8b5cf6',
    cumulativeSoft: '#a78bfa',
    neutral: '#94a3b8',
    grid: '#eef2f6',
    axis: '#64748b',
    aging: ['#10b981', '#22c55e', '#84cc16', '#f59e0b', '#f97316', '#ef4444'],
    agingSoft: ['#34d399', '#4ade80', '#a3e635', '#fbbf24', '#fb923c', '#f87171'],
};

// Vertical gradient helper (top-bottom)
const vGradient = (top: string, bottom: string) =>
    new (echarts as any).graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: top },
        { offset: 1, color: bottom },
    ]);

// Horizontal gradient helper (left-right)
const hGradient = (left: string, right: string) =>
    new (echarts as any).graphic.LinearGradient(0, 0, 1, 0, [
        { offset: 0, color: left },
        { offset: 1, color: right },
    ]);

type ChartViewMode = 'bars' | 'area' | 'line';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('16px'),
        width: '100%',
    },
    headerBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.gap('12px'),
        flexWrap: 'wrap',
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'column',
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('8px'),
        flexWrap: 'wrap',
    },
    activeFilters: {
        display: 'flex',
        ...shorthands.gap('6px'),
        flexWrap: 'wrap',
    },
    kpiGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        ...shorthands.gap('12px'),
    },
    kpiCard: {
        ...shorthands.padding('16px'),
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('6px'),
        position: 'relative',
        overflow: 'hidden',
    },
    kpiHeader: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('8px'),
        color: tokens.colorNeutralForeground3,
    },
    kpiValue: {
        fontSize: '24px',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
    },
    kpiAccentBar: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
    },
    accentBlue: { backgroundColor: tokens.colorBrandBackground },
    accentRed: { backgroundColor: tokens.colorPaletteRedBackground3 },
    accentGreen: { backgroundColor: tokens.colorPaletteGreenBackground3 },
    accentAmber: { backgroundColor: tokens.colorPaletteMarigoldBackground3 },
    chartsGrid: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        ...shorthands.gap('16px'),
        '@media (max-width: 1100px)': {
            gridTemplateColumns: '1fr',
        },
    },
    chartsRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        ...shorthands.gap('16px'),
        '@media (max-width: 1100px)': {
            gridTemplateColumns: '1fr',
        },
    },
    chartCard: {
        ...shorthands.padding('16px'),
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('8px'),
        minHeight: 0,
    },
    chartCardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.gap('8px'),
    },
    emptyState: {
        ...shorthands.padding('32px'),
        textAlign: 'center',
        color: tokens.colorNeutralForeground3,
    },
    loadingOverlay: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.padding('24px'),
    },
    filtersDrawerBody: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('16px'),
    },
    filtersDrawerActions: {
        display: 'flex',
        ...shorthands.gap('8px'),
        justifyContent: 'flex-end',
        marginTop: '12px',
        flexWrap: 'wrap',
    },
    tableWrap: {
        width: '100%',
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: tokens.fontSizeBase300,
    },
    th: {
        textAlign: 'left',
        ...shorthands.padding('8px'),
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        color: tokens.colorNeutralForeground3,
        fontWeight: 600,
    },
    td: {
        ...shorthands.padding('8px'),
        borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    },
    tdRight: {
        ...shorthands.padding('8px'),
        borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
    },
    positive: {
        color: tokens.colorPaletteGreenForeground1,
        fontWeight: 600,
    },
    negative: {
        color: tokens.colorPaletteRedForeground1,
        fontWeight: 600,
    },
    currencyTabs: {
        marginBottom: '8px',
    },
    compositionBody: {
        display: 'grid',
        gridTemplateColumns: '180px 1fr',
        ...shorthands.gap('16px'),
        alignItems: 'center',
        '@media (max-width: 600px)': {
            gridTemplateColumns: '1fr',
        },
    },
    compositionDonut: {
        position: 'relative',
        width: '180px',
        height: '180px',
        '@media (max-width: 600px)': {
            justifySelf: 'center',
        },
    },
    compositionDonutCenter: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        pointerEvents: 'none',
    },
    compositionStats: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('10px'),
    },
    compositionRow: {
        display: 'grid',
        gridTemplateColumns: '12px 1fr auto',
        alignItems: 'center',
        ...shorthands.gap('10px'),
    },
    compositionSwatch: {
        width: '12px',
        height: '12px',
        borderRadius: '3px',
    },
    compositionLabel: {
        display: 'flex',
        flexDirection: 'column',
    },
    compositionValue: {
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'right',
    },
    compositionFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        ...shorthands.padding('10px', '12px'),
        ...shorthands.borderRadius('6px'),
        backgroundColor: tokens.colorNeutralBackground2,
        marginTop: '6px',
    },
    compositionFooterItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    drawerSection: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('12px'),
    },
});

const today = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};
const oneYearBack = () => {
    const d = today();
    d.setFullYear(d.getFullYear() - 1);
    return d;
};

const isoDate = (d?: Date) => (d ? d.toISOString().slice(0, 10) : undefined);

const formatMoney = (v: number, currency: string) => {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || 'USD',
            maximumFractionDigits: 0,
        }).format(v);
    } catch {
        return v.toLocaleString();
    }
};
const formatPercent = (v: number) => `${v.toFixed(1)}%`;

const formatCompactMoney = (v: number, currency: string) => {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || 'USD',
            notation: 'compact',
            maximumFractionDigits: 1,
        }).format(v);
    } catch {
        return v.toLocaleString();
    }
};

const tooltipBase = {
    backgroundColor: 'rgba(255,255,255,0.98)',
    borderColor: '#e5e7eb',
    borderWidth: 1,
    padding: [10, 12],
    textStyle: { color: '#1f2937', fontSize: 12 },
    extraCssText:
        'box-shadow: 0 8px 24px rgba(15,23,42,0.10), 0 2px 4px rgba(15,23,42,0.04); border-radius: 8px; backdrop-filter: blur(8px);',
};

type CombinedProps = ComponentFuncitons.Props;

const ProfitChartComponent: React.FC<CombinedProps> = (props) => {
    const styles = useStyles();
    const { t } = useTranslation();

    // ---------- Filters state ----------
    const [startDate, setStartDate] = useState<Date | undefined>(oneYearBack());
    const [endDate, setEndDate] = useState<Date | undefined>(today());
    const [granularity, setGranularity] = useState<TimeGranularity>('Month');
    const [sponsorId, setSponsorId] = useState<number | undefined>(undefined);
    const [sponsorLabel, setSponsorLabel] = useState<string | undefined>(undefined);
    const [protocolId, setProtocolId] = useState<number | undefined>(undefined);
    const [protocolLabel, setProtocolLabel] = useState<string | undefined>(undefined);
    const [appliedTick, setAppliedTick] = useState(0);

    // ---------- UI state ----------
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [topEntityDetail, setTopEntityDetail] =
        useState<{ kind: 'debtor' | 'creditor'; row: TopEntityStatisticViewModel } | null>(null);
    const [activeCurrency, setActiveCurrency] = useState<string | undefined>(undefined);
    const [orderMode, setOrderMode] = useState<OrderMode>('roi');
    const [seriesViewMode, setSeriesViewMode] = useState<ChartViewMode>('bars');
    const [showCumulative, setShowCumulative] = useState<boolean>(true);

    // ---------- Data state ----------
    const [overview, setOverview] = useState<OverviewStatisticViewModel[]>([]);
    const [paymentDelay, setPaymentDelay] = useState<PaymentDelayStatisticViewModel[]>([]);
    const [monthly, setMonthly] = useState<MonthlyCashflowStatisticViewModel[]>([]);
    const [aging, setAging] = useState<AgingBucketStatisticViewModel[]>([]);
    const [topDebtors, setTopDebtors] = useState<TopEntityStatisticViewModel[]>([]);
    const [topCreditors, setTopCreditors] = useState<TopEntityStatisticViewModel[]>([]);
    const [cashflowSeries, setCashflowSeries] = useState<CashflowSeriesPointViewModel[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | undefined>(undefined);

    // Protocol detail (loaded on demand)
    const [protocolProfits, setProtocolProfits] = useState<ProtocolProfit[]>([]);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    // ---------- Filter builders ----------
    const baseFilter: StatisticsFilter = useMemo(
        () => ({
            startDate: isoDate(startDate),
            endDate: isoDate(endDate),
            sponsorId,
            protocolId,
        }),
        [startDate, endDate, sponsorId, protocolId]
    );

    const seriesFilter: StatisticsTimeSeriesFilter = useMemo(
        () => ({ ...baseFilter, granularity }),
        [baseFilter, granularity]
    );

    // ---------- Initial protocol list load ----------
    useEffect(() => {
        if (!props.MedicalProtocols || props.MedicalProtocols.length === 0) {
            props.GetAllMedicalProtocols();
        }
        if (!props.Persons || props.Persons.length === 0) {
            props.GetAllPersons();
        }
        if (!props.Practitioners || props.Practitioners.length === 0) {
            props.GetAllPractitioners(1, 1000, undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---------- Persons lookup (entityIdentifier -> businessName) ----------
    const currentTenantId = useMemo<string | undefined>(() => {
        if (!props.AccessToken) return undefined;
        try {
            const decoded = jwt_decode.jwtDecode(props.AccessToken) as { tenantId?: string };
            return decoded?.tenantId;
        } catch {
            return undefined;
        }
    }, [props.AccessToken]);

    const personsByIdentifier = useMemo(() => {
        const m = new Map<string, string>();
        (props.Persons || []).forEach((p) => {
            if (p.personIdentifier) {
                m.set(p.personIdentifier.toLowerCase(), p.businessName);
            }
        });
        return m;
    }, [props.Persons]);

    const practitionersByUserId = useMemo(() => {
        const m = new Map<string, string>();
        (props.Practitioners || []).forEach((p) => {
            if (p.userId) {
                const display = p.fullName || p.name || p.userName || p.email || p.userId;
                m.set(p.userId.toLowerCase(), display);
            }
        });
        return m;
    }, [props.Practitioners]);

    const medicalCenterLabel = String(t('ProfitChart.entity.medicalCenter'));

    const resolveEntityName = useCallback(
        (identifier: string | undefined | null): string => {
            if (!identifier) return '';
            const lower = identifier.toLowerCase();
            if (currentTenantId && lower === currentTenantId.toLowerCase()) {
                return medicalCenterLabel;
            }
            const person = personsByIdentifier.get(lower);
            if (person) return person;
            const practitioner = practitionersByUserId.get(lower);
            if (practitioner) return practitioner;
            return identifier;
        },
        [personsByIdentifier, practitionersByUserId, currentTenantId, medicalCenterLabel]
    );

    // ---------- Dashboard load ----------
    useEffect(() => {
        const token = props.AccessToken;
        if (!token) return;

        let cancelled = false;
        setIsLoading(true);
        setLoadError(undefined);

        Promise.all([
            CashFlowStatisticsService.GetOverview(token, baseFilter).catch(() => []),
            CashFlowStatisticsService.GetPaymentDelay(token, baseFilter).catch(() => []),
            CashFlowStatisticsService.GetMonthlyCashflow(token, baseFilter).catch(() => []),
            CashFlowStatisticsService.GetAging(token, baseFilter).catch(() => []),
            CashFlowStatisticsService.GetTopDebtors(token, baseFilter, 10).catch(() => []),
            CashFlowStatisticsService.GetTopCreditors(token, baseFilter, 10).catch(() => []),
            CashFlowStatisticsService.GetCashflowSeries(token, seriesFilter).catch(() => []),
        ])
            .then(([ov, pd, mc, ag, td, tc, cs]) => {
                if (cancelled) return;
                setOverview(ov as OverviewStatisticViewModel[]);
                setPaymentDelay(pd as PaymentDelayStatisticViewModel[]);
                setMonthly(mc as MonthlyCashflowStatisticViewModel[]);
                setAging(ag as AgingBucketStatisticViewModel[]);
                setTopDebtors(td as TopEntityStatisticViewModel[]);
                setTopCreditors(tc as TopEntityStatisticViewModel[]);
                setCashflowSeries(cs as CashflowSeriesPointViewModel[]);
            })
            .catch((err) => {
                if (!cancelled) setLoadError(err?.message || 'Error');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [props.AccessToken, baseFilter, seriesFilter, appliedTick]);

    // ---------- Currency tab handling ----------
    const availableCurrencies = useMemo(() => {
        const set = new Set<string>();
        overview.forEach((o) => set.add(o.currencyCode));
        aging.forEach((a) => set.add(a.currencyCode));
        cashflowSeries.forEach((s) => set.add(s.currencyCode));
        return Array.from(set);
    }, [overview, aging, cashflowSeries]);

    useEffect(() => {
        if (!activeCurrency && availableCurrencies.length > 0) {
            setActiveCurrency(availableCurrencies[0]);
        } else if (activeCurrency && availableCurrencies.length > 0 && !availableCurrencies.includes(activeCurrency)) {
            setActiveCurrency(availableCurrencies[0]);
        }
    }, [availableCurrencies, activeCurrency]);

    const currency = activeCurrency || 'USD';

    const overviewForCurrency = useMemo(
        () => overview.find((o) => o.currencyCode === currency),
        [overview, currency]
    );
    const delayForCurrency = useMemo(
        () => paymentDelay.find((p) => p.currencyCode === currency),
        [paymentDelay, currency]
    );
    const agingForCurrency = useMemo(
        () => aging.filter((a) => a.currencyCode === currency),
        [aging, currency]
    );
    const seriesForCurrency = useMemo(
        () => cashflowSeries.filter((s) => s.currencyCode === currency),
        [cashflowSeries, currency]
    );
    const monthlyForCurrency = useMemo(
        () => monthly.filter((m) => m.currencyCode === currency),
        [monthly, currency]
    );
    const topDebtorsForCurrency = useMemo(
        () => topDebtors.filter((d) => d.currencyCode === currency).slice(0, 8),
        [topDebtors, currency]
    );
    const topCreditorsForCurrency = useMemo(
        () => topCreditors.filter((d) => d.currencyCode === currency).slice(0, 8),
        [topCreditors, currency]
    );

    // ---------- Chart options ----------
    const seriesOption = useMemo(() => {
        if (!seriesForCurrency.length) return null;
        const labels = seriesForCurrency.map((s) => s.periodLabel);
        const incomeData = seriesForCurrency.map((s) => s.income);
        const outcomeData = seriesForCurrency.map((s) => s.outcome);
        const netData = seriesForCurrency.map((s) => s.net);
        const cumulativeData = seriesForCurrency.map((s) => s.cumulativeNet);
        const avgNet = netData.reduce((a, b) => a + b, 0) / Math.max(netData.length, 1);

        const isArea = seriesViewMode === 'area';
        const isLine = seriesViewMode === 'line';
        const flowSeriesType = isArea || isLine ? 'line' : 'bar';

        const baseFlow = {
            type: flowSeriesType as any,
            smooth: isArea || isLine,
            symbol: 'circle',
            symbolSize: 5,
            ...(isArea ? { areaStyle: { opacity: 0.18 } } : {}),
            ...(flowSeriesType === 'bar' ? { barMaxWidth: 22, barGap: '10%' } : {}),
        };

        const legendNames = [
            t('ProfitChart.charts.income'),
            t('ProfitChart.charts.outcome'),
            t('ProfitChart.charts.net'),
        ];
        if (showCumulative) legendNames.push(t('ProfitChart.charts.cumulativeNet'));

        const series: any[] = [
            {
                ...baseFlow,
                name: t('ProfitChart.charts.income'),
                data: incomeData,
                itemStyle: {
                    color: vGradient(CHART_COLORS.incomeSoft, CHART_COLORS.income),
                    borderRadius: [6, 6, 0, 0],
                    shadowColor: 'rgba(16,185,129,0.25)',
                    shadowBlur: 8,
                    shadowOffsetY: 2,
                },
                lineStyle: { width: 2.5, color: CHART_COLORS.income },
                areaStyle: isArea
                    ? { opacity: 0.25, color: vGradient('rgba(16,185,129,0.45)', 'rgba(16,185,129,0.02)') }
                    : undefined,
                emphasis: { focus: 'series', itemStyle: { shadowBlur: 14 } },
            },
            {
                ...baseFlow,
                name: t('ProfitChart.charts.outcome'),
                data: outcomeData,
                itemStyle: {
                    color: vGradient(CHART_COLORS.outcomeSoft, CHART_COLORS.outcome),
                    borderRadius: [6, 6, 0, 0],
                    shadowColor: 'rgba(239,68,68,0.25)',
                    shadowBlur: 8,
                    shadowOffsetY: 2,
                },
                lineStyle: { width: 2.5, color: CHART_COLORS.outcome },
                areaStyle: isArea
                    ? { opacity: 0.25, color: vGradient('rgba(239,68,68,0.45)', 'rgba(239,68,68,0.02)') }
                    : undefined,
                emphasis: { focus: 'series', itemStyle: { shadowBlur: 14 } },
            },
            {
                name: t('ProfitChart.charts.net'),
                type: 'line',
                smooth: true,
                symbol: 'circle',
                symbolSize: 7,
                data: netData,
                itemStyle: {
                    color: CHART_COLORS.net,
                    borderColor: '#ffffff',
                    borderWidth: 2,
                    shadowColor: 'rgba(15,108,189,0.4)',
                    shadowBlur: 6,
                },
                lineStyle: {
                    width: 3,
                    color: hGradient(CHART_COLORS.netSoft, CHART_COLORS.net),
                    shadowColor: 'rgba(15,108,189,0.25)',
                    shadowBlur: 8,
                    shadowOffsetY: 3,
                },
                z: 5,
                emphasis: { scale: 1.4, focus: 'series' },
                markLine: {
                    silent: true,
                    symbol: 'none',
                    lineStyle: { color: CHART_COLORS.neutral, type: 'dashed', width: 1 },
                    label: {
                        formatter: (p: any) =>
                            p.name === 'avg' ? `Ø ${formatCompactMoney(avgNet, currency)}` : '',
                        color: CHART_COLORS.axis,
                        fontSize: 10,
                        fontWeight: 600,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        padding: [2, 6],
                        borderRadius: 4,
                    },
                    data: [
                        { name: 'avg', yAxis: avgNet },
                        { name: 'zero', yAxis: 0, lineStyle: { color: CHART_COLORS.grid, type: 'solid' } },
                    ],
                },
            },
        ];
        if (showCumulative) {
            series.push({
                name: t('ProfitChart.charts.cumulativeNet'),
                type: 'line',
                yAxisIndex: 1,
                smooth: true,
                symbol: 'none',
                data: cumulativeData,
                itemStyle: { color: CHART_COLORS.cumulative },
                lineStyle: {
                    width: 2.5,
                    type: 'dashed',
                    color: CHART_COLORS.cumulative,
                    shadowColor: 'rgba(139,92,246,0.3)',
                    shadowBlur: 6,
                },
                areaStyle: {
                    opacity: 0.18,
                    color: vGradient('rgba(139,92,246,0.35)', 'rgba(139,92,246,0.01)'),
                },
            });
        }

        return {
            backgroundColor: 'transparent',
            animationDuration: 800,
            animationEasing: 'cubicOut' as any,
            color: [CHART_COLORS.income, CHART_COLORS.outcome, CHART_COLORS.net, CHART_COLORS.cumulative],
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross', label: { backgroundColor: '#1f2937' } },
                ...tooltipBase,
                formatter: (params: any[]) => {
                    if (!Array.isArray(params) || !params.length) return '';
                    const lines = params
                        .filter((p) => p.seriesName && p.value != null)
                        .map(
                            (p) =>
                                `<div style="display:flex;justify-content:space-between;gap:14px;"><span>${p.marker}${p.seriesName}</span><b style="font-variant-numeric:tabular-nums;">${formatMoney(p.value, currency)}</b></div>`
                        )
                        .join('');
                    return `<div style="font-weight:600;margin-bottom:4px;">${params[0].axisValueLabel}</div>${lines}`;
                },
            },
            legend: {
                data: legendNames,
                bottom: 0,
                icon: 'roundRect',
                itemWidth: 10,
                itemHeight: 10,
                textStyle: { fontSize: 11, color: CHART_COLORS.axis },
            },
            toolbox: {
                right: 8,
                top: -2,
                itemSize: 12,
                itemGap: 6,
                feature: {
                    dataZoom: { yAxisIndex: 'none', title: { zoom: 'Zoom', back: 'Reset' } },
                    restore: { title: 'Restaurar' },
                    saveAsImage: { title: 'Guardar', name: 'cashflow' },
                },
            },
            grid: { left: 56, right: showCumulative ? 56 : 24, top: 36, bottom: 60, containLabel: false },
            dataZoom: [
                { type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: 'shift' },
                { type: 'slider', xAxisIndex: 0, height: 16, bottom: 28, borderColor: 'transparent', backgroundColor: '#f1f5f9', fillerColor: 'rgba(15,108,189,0.15)', handleStyle: { color: CHART_COLORS.net }, textStyle: { fontSize: 10, color: CHART_COLORS.axis } },
            ],
            xAxis: {
                type: 'category',
                data: labels,
                boundaryGap: flowSeriesType === 'bar',
                axisLine: { lineStyle: { color: CHART_COLORS.grid } },
                axisLabel: { fontSize: 10, color: CHART_COLORS.axis, hideOverlap: true },
                axisTick: { show: false },
            },
            yAxis: [
                {
                    type: 'value',
                    name: currency,
                    nameTextStyle: { fontSize: 10, color: CHART_COLORS.axis },
                    splitLine: { lineStyle: { color: CHART_COLORS.grid, type: 'dashed' } },
                    axisLabel: {
                        fontSize: 10,
                        color: CHART_COLORS.axis,
                        formatter: (v: number) => formatCompactMoney(v, currency),
                    },
                },
                {
                    type: 'value',
                    name: showCumulative ? t('ProfitChart.charts.cumulativeNet') : '',
                    nameTextStyle: { fontSize: 10, color: CHART_COLORS.axis },
                    position: 'right',
                    splitLine: { show: false },
                    show: showCumulative,
                    axisLabel: {
                        fontSize: 10,
                        color: CHART_COLORS.axis,
                        formatter: (v: number) => formatCompactMoney(v, currency),
                    },
                },
            ],
            series,
        };
    }, [seriesForCurrency, currency, t, seriesViewMode, showCumulative]);

    const monthlyOption = useMemo(() => {
        if (!monthlyForCurrency.length) return null;
        const labels = monthlyForCurrency.map((m) => `${m.year}-${String(m.month).padStart(2, '0')}`);
        const netData = monthlyForCurrency.map((m) => (m.income || 0) - (m.outcome || 0));
        return {
            backgroundColor: 'transparent',
            animationDuration: 700,
            animationEasing: 'cubicOut' as any,
            color: [CHART_COLORS.income, CHART_COLORS.outcome, CHART_COLORS.net],
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                ...tooltipBase,
                formatter: (params: any[]) => {
                    if (!Array.isArray(params) || !params.length) return '';
                    const lines = params
                        .map(
                            (p) =>
                                `<div style="display:flex;justify-content:space-between;gap:14px;"><span>${p.marker}${p.seriesName}</span><b style="font-variant-numeric:tabular-nums;">${formatMoney(Math.abs(p.value), currency)}</b></div>`
                        )
                        .join('');
                    return `<div style="font-weight:600;margin-bottom:4px;">${params[0].axisValueLabel}</div>${lines}`;
                },
            },
            legend: {
                data: [t('ProfitChart.charts.income'), t('ProfitChart.charts.outcome'), t('ProfitChart.charts.net')],
                bottom: 0,
                icon: 'roundRect',
                itemWidth: 10,
                itemHeight: 10,
                textStyle: { fontSize: 11, color: CHART_COLORS.axis },
            },
            toolbox: {
                right: 8,
                top: -2,
                itemSize: 12,
                itemGap: 6,
                feature: {
                    dataZoom: { yAxisIndex: 'none', title: { zoom: 'Zoom', back: 'Reset' } },
                    restore: { title: 'Restaurar' },
                    saveAsImage: { title: 'Guardar', name: 'monthly' },
                },
            },
            grid: { left: 56, right: 24, top: 36, bottom: 60 },
            dataZoom: [
                { type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: 'shift' },
                { type: 'slider', xAxisIndex: 0, height: 14, bottom: 28, borderColor: 'transparent', backgroundColor: '#f1f5f9', fillerColor: 'rgba(15,108,189,0.15)', textStyle: { fontSize: 10, color: CHART_COLORS.axis } },
            ],
            xAxis: {
                type: 'category',
                data: labels,
                axisLine: { lineStyle: { color: CHART_COLORS.grid } },
                axisLabel: { fontSize: 10, color: CHART_COLORS.axis, hideOverlap: true },
                axisTick: { show: false },
            },
            yAxis: {
                type: 'value',
                splitLine: { lineStyle: { color: CHART_COLORS.grid, type: 'dashed' } },
                axisLabel: {
                    fontSize: 10,
                    color: CHART_COLORS.axis,
                    formatter: (v: number) => formatCompactMoney(Math.abs(v), currency),
                },
            },
            series: [
                {
                    name: t('ProfitChart.charts.income'),
                    type: 'bar',
                    stack: 'g',
                    barMaxWidth: 24,
                    data: monthlyForCurrency.map((m) => m.income),
                    itemStyle: {
                        color: vGradient(CHART_COLORS.incomeSoft, CHART_COLORS.income),
                        borderRadius: [6, 6, 0, 0],
                        shadowColor: 'rgba(16,185,129,0.25)',
                        shadowBlur: 6,
                    },
                    emphasis: { focus: 'series', itemStyle: { shadowBlur: 12 } },
                },
                {
                    name: t('ProfitChart.charts.outcome'),
                    type: 'bar',
                    stack: 'g',
                    barMaxWidth: 24,
                    data: monthlyForCurrency.map((m) => -m.outcome),
                    itemStyle: {
                        color: vGradient(CHART_COLORS.outcome, CHART_COLORS.outcomeSoft),
                        borderRadius: [0, 0, 6, 6],
                        shadowColor: 'rgba(239,68,68,0.25)',
                        shadowBlur: 6,
                    },
                    emphasis: { focus: 'series', itemStyle: { shadowBlur: 12 } },
                },
                {
                    name: t('ProfitChart.charts.net'),
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 7,
                    data: netData,
                    itemStyle: {
                        color: CHART_COLORS.net,
                        borderColor: '#ffffff',
                        borderWidth: 2,
                        shadowColor: 'rgba(15,108,189,0.35)',
                        shadowBlur: 6,
                    },
                    lineStyle: {
                        width: 3,
                        color: hGradient(CHART_COLORS.netSoft, CHART_COLORS.net),
                        shadowColor: 'rgba(15,108,189,0.25)',
                        shadowBlur: 8,
                        shadowOffsetY: 3,
                    },
                    z: 5,
                    emphasis: { scale: 1.4, focus: 'series' },
                },
            ],
        };
    }, [monthlyForCurrency, t, currency]);

    const agingOption = useMemo(() => {
        if (!agingForCurrency.length) return null;
        const total = agingForCurrency.reduce((s, a) => s + (a.totalPendingAmount || 0), 0) || 1;
        // Horizontal bars sorted by bucket order; better for label readability and quick comparison.
        return {
            backgroundColor: 'transparent',
            animationDuration: 700,
            animationEasing: 'cubicOut' as any,
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                ...tooltipBase,
                formatter: (params: any[]) => {
                    const p = params?.[0];
                    if (!p) return '';
                    const share = ((p.value / total) * 100).toFixed(1);
                    return `<div style="font-weight:600;margin-bottom:4px;">${p.name}</div><div style="display:flex;justify-content:space-between;gap:14px;"><span>${p.marker}${t('ProfitChart.kpi.pending')}</span><b style="font-variant-numeric:tabular-nums;">${formatMoney(p.value, currency)}</b></div><div style="color:#64748b;font-size:11px;margin-top:2px;">${share}% del total</div>`;
                },
            },
            grid: { left: 90, right: 70, top: 12, bottom: 12, containLabel: false },
            xAxis: {
                type: 'value',
                splitLine: { lineStyle: { color: CHART_COLORS.grid, type: 'dashed' } },
                axisLabel: {
                    fontSize: 10,
                    color: CHART_COLORS.axis,
                    formatter: (v: number) => formatCompactMoney(v, currency),
                },
            },
            yAxis: {
                type: 'category',
                data: agingForCurrency.map((a) => a.bucket),
                axisTick: { show: false },
                axisLine: { show: false },
                axisLabel: { fontSize: 11, color: CHART_COLORS.axis, fontWeight: 600 },
            },
            series: [
                {
                    type: 'bar',
                    barMaxWidth: 22,
                    data: agingForCurrency.map((a, i) => {
                        const idx = Math.min(i, CHART_COLORS.aging.length - 1);
                        return {
                            value: a.totalPendingAmount,
                            itemStyle: {
                                color: hGradient(CHART_COLORS.agingSoft[idx], CHART_COLORS.aging[idx]),
                                borderRadius: [6, 6, 6, 6],
                                shadowColor: 'rgba(15,23,42,0.10)',
                                shadowBlur: 6,
                                shadowOffsetX: 2,
                            },
                        };
                    }),
                    emphasis: { itemStyle: { shadowBlur: 14 } },
                    label: {
                        show: true,
                        position: 'right',
                        fontSize: 10,
                        fontWeight: 600,
                        color: CHART_COLORS.axis,
                        formatter: (p: any) => `${formatCompactMoney(p.value, currency)} · ${((p.value / total) * 100).toFixed(0)}%`,
                    },
                },
            ],
        };
    }, [agingForCurrency, currency, t]);

    const topEntityOption = useCallback(
        (rows: TopEntityStatisticViewModel[], variant: 'debtor' | 'creditor') => {
            if (!rows.length) return null;
            // Sort ascending so the largest is at the top of the horizontal bar chart.
            const sorted = [...rows].sort((a, b) => a.totalPendingAmount - b.totalPendingAmount);
            const total = sorted.reduce((s, r) => s + (r.totalPendingAmount || 0), 0) || 1;
            const baseColor = variant === 'debtor' ? CHART_COLORS.outcome : CHART_COLORS.income;
            const softColor = variant === 'debtor' ? CHART_COLORS.outcomeSoft : CHART_COLORS.incomeSoft;
            const shadowRgba = variant === 'debtor' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)';
            return {
                backgroundColor: 'transparent',
                animationDuration: 700,
                animationEasing: 'cubicOut' as any,
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    ...tooltipBase,
                    formatter: (params: any[]) => {
                        const p = params?.[0];
                        if (!p) return '';
                        const share = ((p.value / total) * 100).toFixed(1);
                        return `<div style="font-weight:600;margin-bottom:4px;max-width:240px;white-space:normal;">${p.name}</div><div style="display:flex;justify-content:space-between;gap:14px;"><span>${p.marker}${t('ProfitChart.kpi.pending')}</span><b style="font-variant-numeric:tabular-nums;">${formatMoney(p.value, currency)}</b></div><div style="color:#64748b;font-size:11px;margin-top:2px;">${share}% del total</div>`;
                    },
                },
                grid: { left: 8, right: 70, top: 8, bottom: 8, containLabel: true },
                xAxis: {
                    type: 'value',
                    splitLine: { lineStyle: { color: CHART_COLORS.grid, type: 'dashed' } },
                    axisLabel: {
                        fontSize: 10,
                        color: CHART_COLORS.axis,
                        formatter: (v: number) => formatCompactMoney(v, currency),
                    },
                },
                yAxis: {
                    type: 'category',
                    data: sorted.map((r) => resolveEntityName(r.entityIdentifier)),
                    axisTick: { show: false },
                    axisLine: { show: false },
                    axisLabel: {
                        fontSize: 10,
                        color: CHART_COLORS.axis,
                        width: 110,
                        overflow: 'truncate',
                    },
                },
                series: [
                    {
                        type: 'bar',
                        barMaxWidth: 16,
                        data: sorted.map((r) => r.totalPendingAmount),
                        itemStyle: {
                            color: hGradient(softColor, baseColor),
                            borderRadius: [6, 6, 6, 6],
                            shadowColor: shadowRgba,
                            shadowBlur: 6,
                            shadowOffsetX: 2,
                            opacity: 0.95,
                        },
                        emphasis: { itemStyle: { opacity: 1, shadowBlur: 14 } },
                        label: {
                            show: true,
                            position: 'right',
                            fontSize: 10,
                            fontWeight: 600,
                            color: CHART_COLORS.axis,
                            formatter: (p: any) => formatCompactMoney(p.value, currency),
                        },
                    },
                ],
            };
        },
        [currency, resolveEntityName, t]
    );

    // ---------- Protocol detail loader ----------
    const loadProtocolDetail = useCallback(async () => {
        const token = props.AccessToken;
        if (!token) return;
        const protocols = props.MedicalProtocols || [];
        if (!protocols.length) {
            setProtocolProfits([]);
            return;
        }
        setIsLoadingDetail(true);
        try {
            // baseFilter already carries sponsorId for the backend; Protocol model has no sponsorId field locally.
            const list = protocols.slice(0, 50);
            const results = await Promise.all(
                list.map((p) =>
                    CashFlowStatisticsService.GetMonthlyCashflow(token, {
                        ...baseFilter,
                        protocolId: p.id,
                        currencyCode: currency,
                    })
                        .then((rows: MonthlyCashflowStatisticViewModel[]) => {
                            const revenue = rows.reduce((s, r) => s + (r.income || 0), 0);
                            const cost = rows.reduce((s, r) => s + (r.outcome || 0), 0);
                            const profit = revenue - cost;
                            const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
                            const roi = cost > 0 ? (profit / cost) * 100 : 0;
                            const anyP = p as any;
                            const name =
                                anyP.title ||
                                anyP.fullTitle ||
                                anyP.protocolNumber ||
                                anyP.name ||
                                `#${p.id}`;
                            return {
                                protocolId: p.id,
                                name,
                                revenue,
                                cost,
                                profit,
                                margin,
                                roi,
                            } as ProtocolProfit;
                        })
                        .catch(() => null)
                )
            );
            // Only keep protocols with any cashflow movement; if none, keep empty so the UI shows empty state.
            const filtered = (results.filter(Boolean) as ProtocolProfit[]).filter(
                (r) => r.revenue !== 0 || r.cost !== 0
            );
            setProtocolProfits(filtered);
        } finally {
            setIsLoadingDetail(false);
        }
    }, [props.AccessToken, props.MedicalProtocols, baseFilter, currency]);

    const openDetail = () => {
        setDetailOpen(true);
        if (protocolProfits.length === 0) {
            void loadProtocolDetail();
        }
    };

    const sortedDetail = useMemo(() => {
        const arr = [...protocolProfits];
        if (orderMode === 'margin') arr.sort((a, b) => b.margin - a.margin);
        else if (orderMode === 'roi') arr.sort((a, b) => b.roi - a.roi);
        else arr.sort((a, b) => b.profit - a.profit);
        return arr;
    }, [protocolProfits, orderMode]);

    const detailTreemap = useMemo(() => {
        if (!sortedDetail.length) return null;
        const n = sortedDetail.length;
        const rankColor = (rank: number) => {
            if (n <= 1) return 'rgb(32,173,155)';
            const tt = 1 - rank / (n - 1);
            const c1 = { r: 191, g: 72, b: 43 };
            const c2 = { r: 32, g: 173, b: 155 };
            const r = Math.round(c1.r + (c2.r - c1.r) * tt);
            const g = Math.round(c1.g + (c2.g - c1.g) * tt);
            const b = Math.round(c1.b + (c2.b - c1.b) * tt);
            return `rgb(${r},${g},${b})`;
        };
        return {
            backgroundColor: 'transparent',
            animationDuration: 800,
            animationEasing: 'cubicOut' as any,
            tooltip: {
                trigger: 'item',
                ...tooltipBase,
                formatter: (info: any) => {
                    const d = info.data || {};
                    return [
                        `<b>${d.name || ''}</b>`,
                        `${t('ProfitChart.kpi.revenue')}: ${formatMoney(d.revenue || 0, currency)}`,
                        `${t('ProfitChart.kpi.cost')}: ${formatMoney(d.cost || 0, currency)}`,
                        `${t('ProfitChart.kpi.profit')}: ${formatMoney(d.profit || 0, currency)}`,
                        `${t('ProfitChart.table.margin')}: ${d.margin}%`,
                        `${t('ProfitChart.table.roi')}: ${d.roi}%`,
                    ].join('<br/>');
                },
            },
            series: [
                {
                    type: 'treemap',
                    roam: false,
                    nodeClick: false,
                    breadcrumb: { show: false },
                    label: {
                        show: true,
                        formatter: (info: any) => {
                            const d = info.data || {};
                            const metric =
                                orderMode === 'margin'
                                    ? `${d.margin}%`
                                    : orderMode === 'roi'
                                    ? `${d.roi}%`
                                    : formatMoney(d.profit || 0, currency);
                            return `{name|${info.name}}\n{val|${metric}}`;
                        },
                        rich: {
                            name: { fontSize: 12, fontWeight: 'bold', color: '#fff', lineHeight: 18 },
                            val: { fontSize: 11, color: '#fff', lineHeight: 16 },
                        },
                        overflow: 'truncate',
                    },
                    upperLabel: { show: false },
                    data: sortedDetail.map((d, idx) => {
                        // Size by absolute revenue so the layout reflects business scale;
                        // color encodes the chosen metric ranking.
                        const size = Math.max(d.revenue, 1);
                        return {
                            name: d.name,
                            value: size,
                            revenue: d.revenue,
                            cost: d.cost,
                            profit: d.profit,
                            margin: Number(d.margin.toFixed(2)),
                            roi: Number(d.roi.toFixed(2)),
                            itemStyle: {
                                color: rankColor(idx),
                                borderColor: '#ffffff',
                                borderWidth: 2,
                                gapWidth: 2,
                                shadowColor: 'rgba(15,23,42,0.10)',
                                shadowBlur: 4,
                            },
                        };
                    }),
                },
            ],
        };
    }, [sortedDetail, orderMode, currency, t]);

    // ---------- Handlers ----------
    const handleApply = () => {
        setAppliedTick((v) => v + 1);
        setFiltersOpen(false);
    };
    const handleReset = () => {
        setStartDate(oneYearBack());
        setEndDate(today());
        setGranularity('Month');
        setSponsorId(undefined);
        setSponsorLabel(undefined);
        setProtocolId(undefined);
        setProtocolLabel(undefined);
        setAppliedTick((v) => v + 1);
    };

    const activeFilterCount =
        (sponsorId ? 1 : 0) + (protocolId ? 1 : 0) +
        (startDate?.getTime() !== oneYearBack().getTime() ? 1 : 0) +
        (endDate?.getTime() !== today().getTime() ? 1 : 0);

    // ---------- Render ----------
    return (
        <div className={styles.root}>
            {/* Header bar */}
            <div className={styles.headerBar}>
                <div className={styles.headerLeft}>
                    <Title3>{t('ProfitChart.title')}</Title3>
                    <Caption1>{t('ProfitChart.subtitle')}</Caption1>
                </div>
                <div className={styles.headerActions}>
                    <div className={styles.activeFilters}>
                        {sponsorLabel && (
                            <Badge appearance="tint" color="brand">
                                {t('ProfitChart.filters.sponsor')}: {sponsorLabel}
                            </Badge>
                        )}
                        {protocolLabel && (
                            <Badge appearance="tint" color="brand">
                                {t('ProfitChart.filters.protocol')}: {protocolLabel}
                            </Badge>
                        )}
                    </div>
                    <Button
                        appearance="secondary"
                        icon={<ArrowClockwiseRegular />}
                        onClick={() => setAppliedTick((v) => v + 1)}
                        disabled={isLoading}
                    >
                        {t('ProfitChart.actions.refresh')}
                    </Button>
                    <Button
                        appearance="primary"
                        icon={<FilterRegular />}
                        onClick={() => setFiltersOpen(true)}
                    >
                        {t('ProfitChart.actions.filters')}
                        {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                    </Button>
                    <Button
                        appearance="secondary"
                        icon={<DocumentTableRegular />}
                        onClick={openDetail}
                    >
                        {t('ProfitChart.actions.protocolDetail')}
                    </Button>
                </div>
            </div>

            {/* Currency tabs */}
            {availableCurrencies.length > 1 && (
                <TabList
                    className={styles.currencyTabs}
                    selectedValue={currency}
                    onTabSelect={(_e, data) => setActiveCurrency(data.value as string)}
                >
                    {availableCurrencies.map((c) => (
                        <Tab key={c} value={c}>
                            {c}
                        </Tab>
                    ))}
                </TabList>
            )}

            {/* KPI grid */}
            <div className={styles.kpiGrid}>
                <Card className={styles.kpiCard}>
                    <div className={`${styles.kpiAccentBar} ${styles.accentBlue}`} />
                    <div className={styles.kpiHeader}>
                        <MoneyRegular />
                        <Caption1>{t('ProfitChart.kpi.totalDebt')}</Caption1>
                    </div>
                    <Text className={styles.kpiValue}>
                        {formatMoney(overviewForCurrency?.totalDebtOriginalAmount || 0, currency)}
                    </Text>
                    <Caption1>
                        {overviewForCurrency?.debtCount || 0} {t('ProfitChart.kpi.debts')}
                    </Caption1>
                </Card>
                <Card className={styles.kpiCard}>
                    <div className={`${styles.kpiAccentBar} ${styles.accentGreen}`} />
                    <div className={styles.kpiHeader}>
                        <ArrowDownloadRegular />
                        <Caption1>{t('ProfitChart.kpi.totalPaid')}</Caption1>
                    </div>
                    <Text className={styles.kpiValue}>
                        {formatMoney(overviewForCurrency?.totalDebtPaidAmount || 0, currency)}
                    </Text>
                    <Caption1>
                        {overviewForCurrency?.settledDebtCount || 0} {t('ProfitChart.kpi.settled')}
                    </Caption1>
                </Card>
                <Card className={styles.kpiCard}>
                    <div className={`${styles.kpiAccentBar} ${styles.accentRed}`} />
                    <div className={styles.kpiHeader}>
                        <ArrowTrendingRegular />
                        <Caption1>{t('ProfitChart.kpi.pending')}</Caption1>
                    </div>
                    <Text className={styles.kpiValue}>
                        {formatMoney(overviewForCurrency?.totalDebtPendingAmount || 0, currency)}
                    </Text>
                    <Caption1>
                        {overviewForCurrency?.pendingDebtCount || 0} {t('ProfitChart.kpi.pendingDebts')}
                    </Caption1>
                </Card>
                <Card className={styles.kpiCard}>
                    <div
                        className={`${styles.kpiAccentBar} ${(overviewForCurrency?.netPosition || 0) >= 0 ? styles.accentGreen : styles.accentRed}`}
                    />
                    <div className={styles.kpiHeader}>
                        <DataTrendingRegular />
                        <Caption1>{t('ProfitChart.kpi.netPosition')}</Caption1>
                    </div>
                    <Text
                        className={`${styles.kpiValue} ${(overviewForCurrency?.netPosition || 0) >= 0 ? styles.positive : styles.negative}`}
                    >
                        {formatMoney(overviewForCurrency?.netPosition || 0, currency)}
                    </Text>
                    <Caption1>{currency}</Caption1>
                </Card>
                <Card className={styles.kpiCard}>
                    <div className={`${styles.kpiAccentBar} ${styles.accentAmber}`} />
                    <div className={styles.kpiHeader}>
                        <ArrowClockwiseRegular />
                        <Caption1>{t('ProfitChart.kpi.paymentDelay')}</Caption1>
                    </div>
                    <Text className={styles.kpiValue}>
                        {delayForCurrency ? `${delayForCurrency.averageDelayDays.toFixed(1)} d` : '—'}
                    </Text>
                    <Caption1>
                        {delayForCurrency
                            ? `${t('ProfitChart.kpi.min')} ${delayForCurrency.minDelayDays} · ${t('ProfitChart.kpi.max')} ${delayForCurrency.maxDelayDays}`
                            : ''}
                    </Caption1>
                </Card>
            </div>

            {loadError && (
                <Card className={styles.chartCard}>
                    <Text className={styles.negative}>
                        {t('ProfitChart.errors.loadFailed')}: {loadError}
                    </Text>
                </Card>
            )}

            {isLoading && (
                <div className={styles.loadingOverlay}>
                    <Spinner size="small" label={t('ProfitChart.loading')} />
                </div>
            )}

            {/* Main chart grid: cashflow series + aging side */}
            <div className={styles.chartsGrid}>
                <Card className={styles.chartCard}>
                    <div className={styles.chartCardHeader}>
                        <Subtitle2>{t('ProfitChart.charts.cashflowSeries')}</Subtitle2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <TabList
                                size="small"
                                selectedValue={seriesViewMode}
                                onTabSelect={(_e, data) => setSeriesViewMode(data.value as ChartViewMode)}
                            >
                                <Tab value="bars">Barras</Tab>
                                <Tab value="line">Línea</Tab>
                                <Tab value="area">Área</Tab>
                            </TabList>
                            <Button
                                size="small"
                                appearance={showCumulative ? 'primary' : 'subtle'}
                                onClick={() => setShowCumulative((v) => !v)}
                                title={String(t('ProfitChart.charts.cumulativeNet'))}
                            >
                                Σ
                            </Button>
                            <Caption1>{granularity}</Caption1>
                        </div>
                    </div>
                    {seriesOption ? (
                        <div style={{ width: '100%', height: CHART_HEIGHT_LARGE }}>
                            <ReactECharts
                                notMerge
                                lazyUpdate
                                option={seriesOption}
                                style={{ height: '100%', width: '100%' }}
                            />
                        </div>
                    ) : (
                        <div className={styles.emptyState}>{t('ProfitChart.empty')}</div>
                    )}
                </Card>

                <Card className={styles.chartCard}>
                    <Subtitle2>{t('ProfitChart.charts.aging')}</Subtitle2>
                    {agingOption ? (
                        <div style={{ width: '100%', height: CHART_HEIGHT_MEDIUM }}>
                            <ReactECharts
                                notMerge
                                lazyUpdate
                                option={agingOption}
                                style={{ height: '100%', width: '100%' }}
                            />
                        </div>
                    ) : (
                        <div className={styles.emptyState}>{t('ProfitChart.empty')}</div>
                    )}
                </Card>
            </div>

            {/* Secondary grid: monthly cashflow + top entities */}
            <div className={styles.chartsRow}>
                <Card className={styles.chartCard}>
                    <Subtitle2>{t('ProfitChart.charts.monthlyCashflow')}</Subtitle2>
                    {monthlyOption ? (
                        <div style={{ width: '100%', height: CHART_HEIGHT_MEDIUM }}>
                            <ReactECharts
                                notMerge
                                lazyUpdate
                                option={monthlyOption}
                                style={{ height: '100%', width: '100%' }}
                            />
                        </div>
                    ) : (
                        <div className={styles.emptyState}>{t('ProfitChart.empty')}</div>
                    )}
                </Card>

                <Card className={styles.chartCard}>
                    <div className={styles.chartCardHeader}>
                        <Subtitle2>
                            <BuildingMultipleRegular /> {t('ProfitChart.charts.topDebtors')}
                        </Subtitle2>
                    </div>
                    {topDebtorsForCurrency.length ? (
                        <div style={{ width: '100%', height: CHART_HEIGHT_SMALL }}>
                            <ReactECharts
                                notMerge
                                lazyUpdate
                                option={topEntityOption(topDebtorsForCurrency, 'debtor') || {}}
                                style={{ height: '100%', width: '100%' }}
                                onEvents={{
                                    click: (params: any) => {
                                        const row = topDebtorsForCurrency.find(
                                            (d) => resolveEntityName(d.entityIdentifier) === params?.name
                                        );
                                        if (row) setTopEntityDetail({ kind: 'debtor', row });
                                    },
                                }}
                            />
                        </div>
                    ) : (
                        <div className={styles.emptyState}>{t('ProfitChart.empty')}</div>
                    )}
                </Card>
            </div>

            <div className={styles.chartsRow}>
                <Card className={styles.chartCard}>
                    <div className={styles.chartCardHeader}>
                        <Subtitle2>
                            <BuildingMultipleRegular /> {t('ProfitChart.charts.topCreditors')}
                        </Subtitle2>
                    </div>
                    {topCreditorsForCurrency.length ? (
                        <div style={{ width: '100%', height: CHART_HEIGHT_SMALL }}>
                            <ReactECharts
                                notMerge
                                lazyUpdate
                                option={topEntityOption(topCreditorsForCurrency, 'creditor') || {}}
                                style={{ height: '100%', width: '100%' }}
                                onEvents={{
                                    click: (params: any) => {
                                        const row = topCreditorsForCurrency.find(
                                            (d) => resolveEntityName(d.entityIdentifier) === params?.name
                                        );
                                        if (row) setTopEntityDetail({ kind: 'creditor', row });
                                    },
                                }}
                            />
                        </div>
                    ) : (
                        <div className={styles.emptyState}>{t('ProfitChart.empty')}</div>
                    )}
                </Card>

                <Card className={styles.chartCard}>
                    <div className={styles.chartCardHeader}>
                        <Subtitle2>
                            <MoneyRegular /> {t('ProfitChart.charts.composition')}
                        </Subtitle2>
                    </div>
                    {overviewForCurrency ? (() => {
                        const totalPay = overviewForCurrency.totalPaymentAmount || 0;
                        const imputed = overviewForCurrency.totalPaymentImputedAmount || 0;
                        const pendingImp = overviewForCurrency.totalPaymentPendingImputationAmount || 0;
                        const sumParts = imputed + pendingImp;
                        const imputedShare = sumParts > 0 ? (imputed / sumParts) * 100 : 0;
                        const pendingShare = sumParts > 0 ? (pendingImp / sumParts) * 100 : 0;
                        const colorImputed = tokens.colorPaletteGreenForeground2;
                        const colorPending = tokens.colorPaletteMarigoldForeground2;

                        const donutOption: any = {
                            animationDuration: 800,
                            animationEasing: 'cubicOut',
                            tooltip: {
                                trigger: 'item',
                                ...tooltipBase,
                                formatter: (p: any) =>
                                    `${p.marker} ${p.name}<br/><b style="font-variant-numeric:tabular-nums;">${formatMoney(p.value, currency)}</b> (${p.percent}%)`,
                            },
                            color: [
                                vGradient('#34d399', colorImputed),
                                vGradient('#fbbf24', colorPending),
                            ],
                            series: [
                                {
                                    type: 'pie',
                                    radius: ['62%', '88%'],
                                    avoidLabelOverlap: false,
                                    label: { show: false },
                                    labelLine: { show: false },
                                    silent: sumParts === 0,
                                    itemStyle: {
                                        borderColor: '#ffffff',
                                        borderWidth: 3,
                                        shadowColor: 'rgba(15,23,42,0.10)',
                                        shadowBlur: 10,
                                    },
                                    emphasis: {
                                        scale: true,
                                        scaleSize: 6,
                                        itemStyle: { shadowBlur: 18 },
                                    },
                                    data:
                                        sumParts > 0
                                            ? [
                                                  {
                                                      name: String(t('ProfitChart.kpi.imputed')),
                                                      value: imputed,
                                                  },
                                                  {
                                                      name: String(t('ProfitChart.kpi.pendingImputation')),
                                                      value: pendingImp,
                                                  },
                                              ]
                                            : [
                                                  {
                                                      name: String(t('ProfitChart.empty')),
                                                      value: 1,
                                                      itemStyle: { color: tokens.colorNeutralBackground4 },
                                                  },
                                              ],
                                },
                            ],
                        };

                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
                                <div className={styles.compositionBody}>
                                    <div className={styles.compositionDonut}>
                                        <ReactECharts
                                            notMerge
                                            lazyUpdate
                                            option={donutOption}
                                            style={{ height: '100%', width: '100%' }}
                                        />
                                        <div className={styles.compositionDonutCenter}>
                                            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                                                {t('ProfitChart.kpi.totalPayments')}
                                            </Caption1>
                                            <Body1 style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                                {formatMoney(totalPay, currency)}
                                            </Body1>
                                        </div>
                                    </div>
                                    <div className={styles.compositionStats}>
                                        <div className={styles.compositionRow}>
                                            <span
                                                className={styles.compositionSwatch}
                                                style={{ backgroundColor: colorImputed }}
                                            />
                                            <div className={styles.compositionLabel}>
                                                <Body1>{t('ProfitChart.kpi.imputed')}</Body1>
                                                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                                                    {imputedShare.toFixed(1)}%
                                                </Caption1>
                                            </div>
                                            <Body1 className={styles.compositionValue}>
                                                {formatMoney(imputed, currency)}
                                            </Body1>
                                        </div>
                                        <div className={styles.compositionRow}>
                                            <span
                                                className={styles.compositionSwatch}
                                                style={{ backgroundColor: colorPending }}
                                            />
                                            <div className={styles.compositionLabel}>
                                                <Body1>{t('ProfitChart.kpi.pendingImputation')}</Body1>
                                                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                                                    {pendingShare.toFixed(1)}%
                                                </Caption1>
                                            </div>
                                            <Body1 className={styles.compositionValue}>
                                                {formatMoney(pendingImp, currency)}
                                            </Body1>
                                        </div>
                                    </div>
                                </div>
                                <Divider />
                                <div className={styles.compositionFooter}>
                                    <div className={styles.compositionFooterItem}>
                                        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                                            {t('ProfitChart.kpi.payments')}
                                        </Caption1>
                                        <Body1 style={{ fontWeight: 600 }}>
                                            {overviewForCurrency.paymentCount}
                                        </Body1>
                                    </div>
                                    <div className={styles.compositionFooterItem}>
                                        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                                            {t('ProfitChart.kpi.debts')}
                                        </Caption1>
                                        <Body1 style={{ fontWeight: 600 }}>
                                            {overviewForCurrency.debtCount}
                                        </Body1>
                                    </div>
                                </div>
                            </div>
                        );
                    })() : (
                        <div className={styles.emptyState}>{t('ProfitChart.empty')}</div>
                    )}
                </Card>
            </div>

            {/* ---------------- Filters Drawer ---------------- */}
            <OverlayDrawer
                position="end"
                open={filtersOpen}
                onOpenChange={(_e, data) => setFiltersOpen(data.open)}
                size="medium"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                icon={<DismissRegular />}
                                onClick={() => setFiltersOpen(false)}
                            />
                        }
                    >
                        {t('ProfitChart.filters.title')}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.filtersDrawerBody}>
                        <DateRangeSelectorComponent
                            startDate={startDate}
                            endDate={endDate}
                            onDateChange={(s, e) => {
                                setStartDate(s);
                                setEndDate(e);
                            }}
                            startDateLabel={t('ProfitChart.filters.startDate')}
                            endDateLabel={t('ProfitChart.filters.endDate')}
                        />
                        <Field label={t('ProfitChart.filters.granularity')}>
                            <Dropdown
                                value={t(`ProfitChart.filters.granularityOptions.${granularity.toLowerCase()}`)}
                                selectedOptions={[granularity]}
                                onOptionSelect={(_e, data) =>
                                    data.optionValue && setGranularity(data.optionValue as TimeGranularity)
                                }
                            >
                                {(['Day', 'Week', 'Month', 'Quarter', 'Year'] as TimeGranularity[]).map((g) => {
                                    const label = t(`ProfitChart.filters.granularityOptions.${g.toLowerCase()}`);
                                    return (
                                        <Option key={g} value={g} text={label}>
                                            {label}
                                        </Option>
                                    );
                                })}
                            </Dropdown>
                        </Field>
                        <Field label={t('ProfitChart.filters.sponsor')}>
                            <SponsorSelectorComponent
                                selectionLimit={1}
                                onChangeSelection={(s: any) => {
                                    const v = Array.isArray(s) ? s[0] : s;
                                    setSponsorId(v?.id);
                                    setSponsorLabel(v?.name);
                                }}
                            />
                        </Field>
                        <Field label={t('ProfitChart.filters.protocol')}>
                            <ProtocolSelectorComponent
                                selectionLimit={1}
                                onChangeSelection={(protocolsSelected: any[] | undefined) => {
                                    const v = (protocolsSelected || [])[0];
                                    setProtocolId(v?.id);
                                    setProtocolLabel(v?.name);
                                }}
                            />
                        </Field>
                        <div className={styles.filtersDrawerActions}>
                            <Button
                                appearance="secondary"
                                icon={<ArrowResetRegular />}
                                onClick={handleReset}
                            >
                                {t('ProfitChart.filters.reset')}
                            </Button>
                            <Button
                                appearance="primary"
                                icon={<ArrowClockwiseRegular />}
                                onClick={handleApply}
                            >
                                {t('ProfitChart.filters.apply')}
                            </Button>
                        </div>
                    </div>
                </DrawerBody>
            </OverlayDrawer>

            {/* ---------------- Protocol Detail Drawer ---------------- */}
            <OverlayDrawer
                position="end"
                open={detailOpen}
                onOpenChange={(_e, data) => setDetailOpen(data.open)}
                size="large"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                icon={<DismissRegular />}
                                onClick={() => setDetailOpen(false)}
                            />
                        }
                    >
                        {t('ProfitChart.detail.title')} · {currency}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerSection}>
                        <Field label={t('ProfitChart.filters.order')}>
                            <Dropdown
                                value={t(`ProfitChart.orderOptions.${orderMode}`)}
                                selectedOptions={[orderMode]}
                                onOptionSelect={(_e, data) =>
                                    data.optionValue && setOrderMode(data.optionValue as OrderMode)
                                }
                            >
                                <Option value="margin" text={String(t('ProfitChart.orderOptions.margin'))}>
                                    {t('ProfitChart.orderOptions.margin')}
                                </Option>
                                <Option value="roi" text={String(t('ProfitChart.orderOptions.roi'))}>
                                    {t('ProfitChart.orderOptions.roi')}
                                </Option>
                                <Option value="profit" text={String(t('ProfitChart.orderOptions.profit'))}>
                                    {t('ProfitChart.orderOptions.profit')}
                                </Option>
                            </Dropdown>
                        </Field>
                        <div>
                            <Button
                                appearance="secondary"
                                icon={<ArrowClockwiseRegular />}
                                onClick={loadProtocolDetail}
                                disabled={isLoadingDetail}
                            >
                                {t('ProfitChart.detail.reload')}
                            </Button>
                        </div>

                        {isLoadingDetail ? (
                            <div className={styles.loadingOverlay}>
                                <Spinner size="small" label={t('ProfitChart.loading')} />
                            </div>
                        ) : (
                            <>
                                {detailTreemap ? (
                                    <div style={{ width: '100%', height: 320 }}>
                                        <ReactECharts
                                            notMerge
                                            lazyUpdate
                                            option={detailTreemap}
                                            style={{ height: '100%', width: '100%' }}
                                        />
                                    </div>
                                ) : (
                                    <div className={styles.emptyState}>{t('ProfitChart.empty')}</div>
                                )}

                                {sortedDetail.length > 0 && (
                                    <div className={styles.tableWrap}>
                                        <table className={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th className={styles.th}>{t('ProfitChart.table.protocol')}</th>
                                                    <th className={styles.th} style={{ textAlign: 'right' }}>
                                                        {t('ProfitChart.kpi.revenue')}
                                                    </th>
                                                    <th className={styles.th} style={{ textAlign: 'right' }}>
                                                        {t('ProfitChart.kpi.cost')}
                                                    </th>
                                                    <th className={styles.th} style={{ textAlign: 'right' }}>
                                                        {t('ProfitChart.kpi.profit')}
                                                    </th>
                                                    <th className={styles.th} style={{ textAlign: 'right' }}>
                                                        {t('ProfitChart.table.margin')}
                                                    </th>
                                                    <th className={styles.th} style={{ textAlign: 'right' }}>
                                                        {t('ProfitChart.table.roi')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedDetail.map((p) => (
                                                    <tr key={p.protocolId}>
                                                        <td className={styles.td}>{p.name}</td>
                                                        <td className={styles.tdRight}>
                                                            {formatMoney(p.revenue, currency)}
                                                        </td>
                                                        <td className={styles.tdRight}>
                                                            {formatMoney(p.cost, currency)}
                                                        </td>
                                                        <td
                                                            className={`${styles.tdRight} ${p.profit >= 0 ? styles.positive : styles.negative}`}
                                                        >
                                                            {formatMoney(p.profit, currency)}
                                                        </td>
                                                        <td
                                                            className={`${styles.tdRight} ${p.margin >= 0 ? styles.positive : styles.negative}`}
                                                        >
                                                            {formatPercent(p.margin)}
                                                        </td>
                                                        <td
                                                            className={`${styles.tdRight} ${p.roi >= 0 ? styles.positive : styles.negative}`}
                                                        >
                                                            {formatPercent(p.roi)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </DrawerBody>
            </OverlayDrawer>

            {/* ---------------- Top Entity Detail Drawer ---------------- */}
            <OverlayDrawer
                position="end"
                open={!!topEntityDetail}
                onOpenChange={(_e, data) => !data.open && setTopEntityDetail(null)}
                size="small"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                icon={<DismissRegular />}
                                onClick={() => setTopEntityDetail(null)}
                            />
                        }
                    >
                        {topEntityDetail?.kind === 'debtor'
                            ? t('ProfitChart.charts.topDebtors')
                            : t('ProfitChart.charts.topCreditors')}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    {topEntityDetail && (
                        <div className={styles.drawerSection}>
                            <Subtitle2>{resolveEntityName(topEntityDetail.row.entityIdentifier)}</Subtitle2>
                            <Body1>
                                {t('ProfitChart.kpi.pending')}:{' '}
                                <b>{formatMoney(topEntityDetail.row.totalPendingAmount, currency)}</b>
                            </Body1>
                            <Body1>
                                {t('ProfitChart.kpi.totalPaid')}:{' '}
                                <b>{formatMoney(topEntityDetail.row.totalPaidAmount, currency)}</b>
                            </Body1>
                            <Body1>
                                {t('ProfitChart.kpi.totalDebt')}:{' '}
                                <b>{formatMoney(topEntityDetail.row.totalOriginalAmount, currency)}</b>
                            </Body1>
                            <Body1>
                                {t('ProfitChart.kpi.debts')}: <b>{topEntityDetail.row.debtCount}</b>
                            </Body1>
                            <Body1>
                                {t('ProfitChart.kpi.share')}:{' '}
                                <b>{formatPercent(topEntityDetail.row.sharePercentage)}</b>
                            </Body1>
                        </div>
                    )}
                </DrawerBody>
            </OverlayDrawer>
        </div>
    );
};

export default connect(
    ComponentFuncitons.mapStateToProps,
    ComponentFuncitons.mapDispatchToProps
)(ProfitChartComponent as any);
